import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, Loader2, Shield, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ExcludedDomain {
  id: string;
  domain: string;
  reason: string | null;
  created_at: string;
  is_active: boolean;
}

interface ExcludedDomainsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExcludedDomainsManager({ open, onOpenChange }: ExcludedDomainsManagerProps) {
  const [domains, setDomains] = useState<ExcludedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [newReason, setNewReason] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('excluded_domains')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDomains(data || []);
    } catch (error) {
      console.error('Error loading excluded domains:', error);
      toast.error('Błąd ładowania domen wykluczonych');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Wprowadź domenę');
      return;
    }

    try {
      setAdding(true);
      const { error } = await supabase
        .from('excluded_domains')
        .insert({
          domain: newDomain.trim().toLowerCase(),
          reason: newReason.trim() || null,
          is_active: true
        });

      if (error) {
        if (error.code === '23505') {
          toast.error('Ta domena już istnieje w bazie');
        } else {
          throw error;
        }
        return;
      }

      toast.success('Domena została dodana');
      setNewDomain('');
      setNewReason('');
      loadDomains();
    } catch (error) {
      console.error('Error adding domain:', error);
      toast.error('Błąd dodawania domeny');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteDomain = async (id: string, domain: string) => {
    if (!confirm(`Czy na pewno chcesz usunąć domenę: ${domain}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('excluded_domains')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Domena została usunięta');
      loadDomains();
    } catch (error) {
      console.error('Error deleting domain:', error);
      toast.error('Błąd usuwania domeny');
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from('excluded_domains')
        .update({ is_active: !currentActive })
        .eq('id', id);

      if (error) throw error;

      toast.success(currentActive ? 'Domena wyłączona' : 'Domena włączona');
      loadDomains();
    } catch (error) {
      console.error('Error toggling domain:', error);
      toast.error('Błąd zmiany statusu domeny');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Domeny wykluczane
          </DialogTitle>
          <DialogDescription>
            Zarządzaj listą domen wykluczanych z wyszukiwania konkurencji
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Domeny z tej listy będą wykluczane z wyników wyszukiwania konkurencji.
            Obsługiwane są wzorce z gwiazdką (np. *.edu.pl).
          </AlertDescription>
        </Alert>

        {/* Add new domain form */}
        <div className="grid gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="grid gap-2">
            <Label htmlFor="new-domain">Nowa domena</Label>
            <Input
              id="new-domain"
              placeholder="np. example.com lub *.edu.pl"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="new-reason">Powód wykluczenia (opcjonalnie)</Label>
            <Input
              id="new-reason"
              placeholder="np. Portal edukacyjny"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            />
          </div>
          <Button 
            onClick={handleAddDomain} 
            disabled={adding || !newDomain.trim()}
            className="w-full"
          >
            {adding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Dodawanie...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Dodaj domenę
              </>
            )}
          </Button>
        </div>

        {/* Domains list */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : domains.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Brak wykluczonych domen
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domena</TableHead>
                  <TableHead>Powód</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dodano</TableHead>
                  <TableHead className="text-right">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((domain) => (
                  <TableRow key={domain.id} className={!domain.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-mono text-sm">
                      {domain.domain}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {domain.reason || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={domain.is_active ? 'default' : 'secondary'}
                        className="cursor-pointer"
                        onClick={() => handleToggleActive(domain.id, domain.is_active)}
                      >
                        {domain.is_active ? 'Aktywna' : 'Nieaktywna'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(domain.created_at).toLocaleDateString('pl-PL')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDomain(domain.id, domain.domain)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="text-xs text-muted-foreground pt-2">
          Łącznie wykluczonych domen: <strong>{domains.filter(d => d.is_active).length}</strong> aktywnych / {domains.length} wszystkich
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
